<!-- src/pages/purchase/tickets.vue -->
<script setup>
import { jwtDecode } from 'jwt-decode';
import TicketList from '@/components/purchase/TicketList.vue';
import GiftList from '@/components/purchase/GiftList.vue';
import PopGiftView from '@/components/purchase/PopGiftView.vue';
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import Pagination from '@/components/board/Pagination.vue';
import IconHeaderClose from '@/components/svg/IconHeaderClose.vue';
import MypageLnbTabMenu from '@/components/MypageLnbTabMenu.vue';
import { getGiftInbox, getGiftOutbox, validateGiftCode } from '@/api/gifts';
import { useRnBridge } from '@/composables/useRnBridge';
import { canonicalUrl, applySeoMeta } from '@/utils/seo';

import { useProductThumbnail } from '@/composables/useProductThumbnail';
import { gnbMenuLinks } from '@/constants/menuLinks';
import { gnbContentTypeMap as contentTypeMap } from '@/constants/logContentType';
import { addContentLogAPI } from '@/api/logServiceApi';
import { useStore } from 'vuex';
import { useMyInfoStore } from '@/store/modules/myinfo';
import { clearMySajuState } from '@/composables/calendar/mySaju/useMySaju';
import DropdownCreditInfo from '@/components/common/DropdownCreditInfo.vue';

definePage({ meta: { layout: 'purchase', title: '분석권 구매' } });

const creditInfoRef = ref(null);

const router = useRouter();
const route = useRoute();
const store = useStore();

applySeoMeta({
  title: '사주분석할인, 용신, 궁합, 성향, 택일',
  description:
    '용신·성향·궁합·신년운세 분석권을 최대 60% 할인 세트로, 낭만 맞춤택일과 패키지 상품까지. 여러 사주 분석이 필요한 분들께 유리합니다.',
  url: canonicalUrl(route.fullPath),
});
const myInfoStore = useMyInfoStore();
const {
  dialogTitle,
  dialogTitle2,
  dialogText,
  dialogWarning,
  dialogNextUrl,
  alertRef,
  confirmRef,
  showConfirm,
} = useDialogManager();
const refreshKey = ref(0);
const isWebView = ref(false);
const isMobileViewport = ref(false);

const initialTopTab =
  route.query.tab === 'gift' || route.query.tab === 'inbox' || route.query.tab === 'outbox'
    ? 'gift'
    : 'tickets';
const activeTopTab = ref(initialTopTab);

const initialGiftTab =
  route.query.giftTab === 'outbox' || route.query.tab === 'outbox' ? 'outbox' : 'inbox';
const initialGiftPage = (() => {
  const parsed = Number.parseInt(`${route.query.giftPage || route.query.page || 1}`, 10);
  return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
})();

const giftTabType = ref(initialGiftTab);
const giftList = ref([]);
const giftLoading = ref(false);
const giftTotalPages = ref(0);
const giftFetchParams = ref({
  page: initialGiftPage,
  size: 10,
  grantType: 'GIFT_DIRECT',
});
const selectedGift = ref(null);
const giftViewRef = ref(null);

const { sendMessageToApp } = useRnBridge();
const { ensureThumbnailMap } = useProductThumbnail();
const isGiftLoggedIn = computed(() => {
  const userId = store.getters['user/id'];
  return !!userId || !!sessionStorage.getItem('X-Token');
});

const isInlineGiftTab = computed(
  () => (isWebView.value || isMobileViewport.value) && activeTopTab.value === 'gift',
);

const updateViewport = () => {
  if (typeof window === 'undefined') return;
  isMobileViewport.value = window.innerWidth <= 768;
};

const fetchGiftList = async () => {
  if (!isGiftLoggedIn.value) {
    giftLoading.value = false;
    giftList.value = [];
    giftTotalPages.value = 0;
    return;
  }

  giftLoading.value = true;
  try {
    await ensureThumbnailMap();

    const result =
      giftTabType.value === 'inbox'
        ? await getGiftInbox(giftFetchParams.value)
        : await getGiftOutbox(giftFetchParams.value);

    giftList.value = result.content || [];
    giftTotalPages.value = result.totalPages || 0;
  } catch (error) {
    console.error('선물 목록 조회 실패:', error);
  } finally {
    giftLoading.value = false;
  }
};

const changeGiftTab = async (type) => {
  if (giftTabType.value === type) return;
  giftTabType.value = type;
  giftFetchParams.value.page = 1;
  await fetchGiftList();
};

const changeGiftPage = async (page) => {
  giftFetchParams.value.page = page;
  await fetchGiftList();
};

const handleGiftClick = async (item) => {
  if (giftTabType.value === 'outbox' || item.status === 'ACCEPTED') {
    try {
      const result = await validateGiftCode(item.code);
      selectedGift.value = result;
      giftViewRef.value?.open();
    } catch (error) {
      console.error('선물 정보 조회 실패:', error);
    }
    return;
  }

  router.push({
    path: '/purchase/gift-claim',
    query: { code: item.code },
  });
};

const openTicketTab = () => {
  activeTopTab.value = 'tickets';
};

const isTokenValid = () => {
  const token = sessionStorage.getItem('X-Token');
  if (!token) return false;
  try {
    const { exp } = jwtDecode(token);
    return exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

const confirmLoginForGiftBox = () => {
  if (isWebView.value) {
    sendMessageToApp({ action: 'login' });
    return;
  }
  router.push({ path: '/auth/login', query: { to: '/purchase/gift-box' } });
};

const openGiftTab = async () => {
  if (!isWebView.value && (!isLoggedIn.value || !isTokenValid())) {
    showConfirm({
      title: '로그인 후 이용 가능합니다.',
      title2: '로그인 페이지로 이동하시겠습니까?',
      text: '확인',
      onConfirm: () => confirmLoginForGiftBox(),
    });
    return;
  }
  await router.push('/purchase/gift-box');
};

const handlePointTab = () => {
  router.push('/purchase/points');
};

onMounted(async () => {
  // 서비스 구매(분석권) 페이지 진입 로그 (신규 type, DB 등록 후 작동)
  addContentLogAPI('S_service_purchase_view');
  isWebView.value = !!window.ReactNativeWebView;
  updateViewport();
  window.addEventListener('resize', updateViewport);

  if (activeTopTab.value === 'gift') {
    await fetchGiftList();
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', updateViewport);
});

watch(
  () => isGiftLoggedIn.value,
  async (loggedIn) => {
    if (!isInlineGiftTab.value) return;
    if (loggedIn) {
      await fetchGiftList();
    } else {
      giftList.value = [];
      giftTotalPages.value = 0;
    }
  },
);

watch(
  () => isMobileViewport.value,
  (isMobile) => {
    if (!isMobile && !isWebView.value && activeTopTab.value === 'gift') {
      activeTopTab.value = 'tickets';
    }
  },
);

const handleClose = () => {
  router.replace('/main');
};

// --- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) ---
const isMypageMenuOpen = ref(false);

const toggleMypageMenu = () => {
  isMypageMenuOpen.value = !isMypageMenuOpen.value;
};

const closeMypageMenu = () => {
  isMypageMenuOpen.value = false;
};

watch(
  () => route.path,
  () => {
    if (isMypageMenuOpen.value) {
      isMypageMenuOpen.value = false;
    }
  },
);

const isLoggedIn = computed(() => {
  const userId = store.getters['user/id'];
  const userName = store.getters['user/name'];
  try {
    const userInfo = JSON.parse(sessionStorage.getItem('userInfo') || 'null');
    return !!(userId || userName || userInfo?.name);
  } catch {
    return !!(userId || userName);
  }
});

const displayName = computed(() => {
  if (isLoggedIn.value) {
    return myInfoStore?.displayName || store.getters['user/name'] || '';
  }
  return '';
});
const isHomeRoute = computed(() => route.path === '/main' || route.path === '/');

const handleGoHome = () => {
  closeMypageMenu();
  if (!isHomeRoute.value) {
    router.push('/main');
  }
};

const handleLogin = () => {
  if (isWebView.value) {
    sendMessageToApp({ action: 'login' });
    return;
  }

  addContentLogAPI('gnb_btn_join');
  router.push({
    path: '/auth/login',
    query: { to: route.fullPath },
  });
  closeMypageMenu();
};

const isMenuActive = (menuLink) => {
  const currentPath = route.path;
  switch (menuLink.name) {
    case '낭만분석':
      return (
        currentPath.startsWith('/saju-service') ||
        currentPath.startsWith('/saju-result') ||
        currentPath.startsWith('/saju-insight') ||
        currentPath.startsWith('/analysis')
      );
    default:
      return currentPath.startsWith(menuLink.basePath || menuLink.link);
  }
};

const handleMenuClick = (menu) => {
  addContentLogAPI(contentTypeMap[menu.link]);
  if (menu.name !== '낭만만세력은?') {
    router.push(menu.link);
  }
  closeMypageMenu();
};

const handleLogout = async () => {
  addContentLogAPI('pi_btn_logout');
  try {
    await store.dispatch('user/logout');
    clearMySajuState();
    await router.push('/auth/login');
    myInfoStore.resetSelectedOption();
    closeMypageMenu();
  } catch (error) {
    console.error('Logout error:', error);
  }
};
</script>

<template>
  <div class="my-info-wrap purchase-ticket">
    <div class="my-tit-box com-bg-my-box st-guide com-pc-title-box">
      <div class="tit">낭만 분석권 구매</div>
    </div>

    <div
      class="com-app-tit-box purchase-service-header"
      :class="{ 'purchase-service-header--app': isWebView }"
    >
      <div class="tit">서비스 구매</div>
      <div class="btn-right">
        <button v-if="isWebView" class="btn-back" @click="handleClose">
          <IconHeaderClose />
        </button>
        <button v-else class="btn-mypage" @click="toggleMypageMenu">
          <i class="icon-mypage"></i>
        </button>
      </div>
    </div>

    <div class="com-app-tab-box purchase-service-tabs">
      <button
        class="tab-button"
        :class="{ selected: activeTopTab === 'tickets' }"
        @click="openTicketTab"
      >
        분석권 구매
      </button>
      <button class="tab-button" @click="handlePointTab">포인트 충전</button>
      <button
        class="tab-button"
        :class="{ selected: activeTopTab === 'gift' }"
        @click="openGiftTab"
      >
        선물함
      </button>
    </div>

    <div class="myinfo-box com-bg-my-box product">
      <template v-if="isInlineGiftTab">
        <div class="purchase-gift-inline">
          <div class="purchase-gift-inline__tabs">
            <button
              class="purchase-gift-inline__tab"
              :class="{ active: giftTabType === 'inbox' }"
              @click="changeGiftTab('inbox')"
            >
              받은 선물
            </button>
            <button
              class="purchase-gift-inline__tab"
              :class="{ active: giftTabType === 'outbox' }"
              @click="changeGiftTab('outbox')"
            >
              보낸 선물
            </button>
          </div>

          <div v-if="giftLoading" class="gift-loading-box">
            <div class="loading-spinner"></div>
          </div>

          <template v-else-if="giftList.length > 0">
            <div class="purchase-gift-inline__list">
              <GiftList
                :items="giftList"
                :wide-layout="false"
                :type="giftTabType"
                @click-item="handleGiftClick"
              />
            </div>

            <Pagination
              v-if="giftTotalPages > 1"
              :total-pages="giftTotalPages"
              :current-page="giftFetchParams.page"
              @page-changed="changeGiftPage"
            />
          </template>

          <div v-else class="gift-none-box">
            <div class="txt1">
              {{ giftTabType === 'outbox' ? '보낸 선물이 없습니다.' : '받은 선물이 없습니다.' }}
            </div>
            <button
              v-if="giftTabType === 'outbox'"
              class="gift-none-box__action"
              @click="openTicketTab"
            >
              선물하러 가기
            </button>
            <button
              v-if="giftTabType === 'inbox' && !isGiftLoggedIn"
              class="gift-none-box__action"
              @click="handleLogin"
            >
              로그인 후 이용하기
            </button>
          </div>
        </div>
      </template>
      <template v-else>
        <TicketList :refresh-key="refreshKey" />
      </template>
    </div>

    <PopGiftView ref="giftViewRef" :gift-info="selectedGift" :type="giftTabType" />

    <!-- 마이페이지 드롭다운 메뉴 (모바일 웹 전용) -->
    <Teleport to="body">
      <div
        v-if="isMypageMenuOpen && !isWebView"
        class="mypage-menu-overlay"
        @click="closeMypageMenu"
      >
        <div class="mypage-menu-dropdown" @click.stop>
          <div class="login-msg-box" @click="creditInfoRef?.closeDropdown()">
            <button class="btn-home" :class="{ active: isHomeRoute }" @click="handleGoHome">
              <i class="icon-home"></i>
              홈
            </button>
            <template v-if="isLoggedIn">
              <div class="txt-box">
                <span
                  ><span class="txt1" style="margin-right: 2px">{{ displayName || '게스트' }}</span
                  >님</span
                >
                <span class="txt2">행복한 하루 되세요</span>
              </div>
            </template>
            <template v-else>
              <div class="txt-box">
                <span class="txt1">게스트</span>
                <span class="txt2">로그인 후 이용해주세요</span>
              </div>
            </template>

            <DropdownCreditInfo
              v-if="isLoggedIn"
              ref="creditInfoRef"
              :is-logged-in="isLoggedIn"
              :visible="isMypageMenuOpen"
              @close-menu="closeMypageMenu"
            />

            <div class="gnb-menu-list">
              <template v-for="(menu, key) in gnbMenuLinks" :key="key">
                <router-link
                  v-if="menu.name !== '낭만만세력은?'"
                  :to="menu.link"
                  :class="{ active: isMenuActive(menu) }"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                >
                  {{ menu.name }}
                </router-link>
                <a
                  v-else
                  :href="$router.resolve(menu.link).href"
                  target="_blank"
                  rel="noopener"
                  class="gnb-menu-item"
                  @click="handleMenuClick(menu)"
                >
                  {{ menu.name }}
                </a>
              </template>
            </div>

            <template v-if="isLoggedIn">
              <MypageLnbTabMenu />
              <button class="btn-xs line" @click="handleLogout">로그아웃</button>
            </template>
            <template v-else>
              <button class="btn-xs line login-btn" @click="handleLogin">로그인</button>
            </template>
          </div>
        </div>
      </div>
    </Teleport>

    <ConfirmComponent
      ref="confirmRef"
      :title="dialogTitle"
      :title2="dialogTitle2"
      :text="dialogText"
      :warning="dialogWarning"
      :nextUrl="dialogNextUrl"
    />
  </div>
</template>

<style lang="scss">
@import '@/assets/webview/guide_product.scss';

@media (max-width: 768px) {
  .purchase-ticket {
    .myinfo-box.com-bg-my-box.product {
      margin-top: 0;
      border-radius: 0;
      background: #fff;
    }

    .ticket-category-area {
      background: #fff;

      .board__search-results {
        display: flex;
        padding: 8px 16px 0px !important;
        margin-top: 0 !important;
        margin-bottom: 0 !important;
      }
    }

    .category-list-box {
      margin-bottom: 0 !important;
      padding: 10px 16px 8px;
      gap: 6px;
    }

    .btn-category {
      margin-right: 0;
      padding: 6px 12px;
      border-radius: 16px;
      border: 1px solid transparent;
      background: #f8f8f8;
      color: #949494;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;

      &.active {
        border-color: #eb4c10;
        background: #eb4c10;
        color: #fff;
        font-weight: 700;
      }
    }

    .product-card-box {
      gap: 20px;
      padding: 12px 16px 28px;

      .row-box {
        flex: 0 0 100%;
        max-width: 100%;

        .text-box {
          .txt-like {
            top: 12px;
            right: 12px;
          }

          .txt1 {
            padding-top: 0;
          }
        }
      }
    }
  }

  .purchase-service-header {
    justify-content: center;
    border-bottom: 1px solid #ebebeb;

    .btn-right {
      position: absolute;
      right: 8px;
      display: flex;
      align-items: center;
      gap: 0;
    }

    .btn-right .btn-back {
      display: inline-flex;
      width: 24px;
      height: 24px;
      align-items: center;
      justify-content: center;
      border: 0;
      background: transparent;
      padding: 0;
      cursor: pointer;
    }

    .btn-right .btn-mypage {
      display: inline-flex;
      width: 36px;
      height: 36px;
      align-items: center;
      justify-content: center;
      border: none;
      background: transparent;
      padding: 0;
      cursor: pointer;
    }

    .btn-right .btn-mypage .icon-mypage {
      display: flex;
      width: 32px;
      height: 32px;
      border-radius: 100%;
      background: url('@manseImages/icon-mypage.png') center center no-repeat;
      background-size: 32px auto;
      font-size: 0;
    }

    &.purchase-service-header--app {
      .btn-right {
        right: 16px;
      }
    }
  }

  .purchase-service-tabs {
    height: 44px;
    padding: 0 15px;
    border-bottom: 1px solid #ebebeb;
    justify-content: space-between;

    .tab-button {
      flex: 1;
      max-width: none;
      padding: 6px 0;
      font-size: 16px;
      font-weight: 500;
      line-height: 30px;
    }
  }

  .purchase-gift-inline {
    min-height: 100%;
    background: #f8f8f8;

    &__tabs {
      display: flex;
      gap: 6px;
      padding: 10px 16px 8px;
      background: #fff;
    }

    &__tab {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 6px 12px;
      border-radius: 16px;
      border: 1px solid #eb4c10;
      background: #fff;
      color: #eb4c10;
      font-size: 14px;
      font-weight: 500;
      line-height: 18px;

      &.active {
        background: #eb4c10;
        color: #fff;
        font-weight: 700;
      }
    }

    &__list {
      padding: 12px 16px 28px;
    }

    .gift-list {
      margin: 0;
      gap: 20px;

      .gift-card {
        flex: 0 0 100%;
        max-width: 100%;
      }

      .gift-card__thumbnail img {
        object-fit: cover;
      }

      .gift-card__content {
        padding: 20px 19px;
      }

      .gift-card__title {
        font-size: 18px;
      }

      .gift-card__subtitle {
        font-size: 13px;
        line-height: 18px;
      }
    }

    .gift-loading-box,
    .gift-none-box {
      min-height: calc(100vh - 220px);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 16px;
    }

    .gift-none-box {
      .txt1 {
        color: #757575;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
      }

      &__action {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        padding: 6px 10px;
        border-radius: 6px;
        border: 1px solid #eb4c10;
        background: #fff;
        color: #eb4c10;
        font-size: 12px;
        font-weight: 700;
        line-height: 14px;
      }
    }

    .gift-loading-box .loading-spinner {
      width: 40px;
      height: 40px;
      border: 3px solid #e5e5e5;
      border-top-color: #eb4c10;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  .mypage-menu-overlay {
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: transparent;
    z-index: 998;
  }

  .mypage-menu-dropdown {
    display: block;
    position: fixed;
    top: 60px;
    right: 16px;
    width: 280px;
    max-height: calc(100vh - 76px);
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 999;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;

    .login-msg-box {
      position: relative;
      padding: 20px;
      width: 100%;
      box-sizing: border-box;

      .btn-home {
        position: absolute;
        top: 14px;
        right: 14px;
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 28px;
        padding: 0 8px;
        border: 1px solid #e6e6e6;
        border-radius: 14px;
        background: #fff;
        color: #333;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;

        .icon-home {
          width: 14px;
          height: 14px;
          background-color: currentColor;
          -webkit-mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
          mask: url('@manseImages/icon_home.svg') center center / contain no-repeat;
        }

        &.active {
          color: #eb4c10;
          border-color: #f7d1c3;
          background: #fff5f2;
        }
      }

      .txt-box {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        color: #333;
        font-size: 15px;
        margin-bottom: 16px;
        padding-top: 6px;

        .txt1 {
          font-weight: 700;
        }

        .txt2 {
          color: #757575;
          font-size: 13px;
          margin-top: 4px;
        }
      }

      .gnb-menu-list {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        margin-bottom: 16px;
        padding-top: 12px;
        border-top: 1px solid #f5f5f5;

        .gnb-menu-item {
          display: inline-block;
          padding: 6px 12px;
          color: #333;
          font-family: Pretendard;
          font-size: 13px;
          font-weight: 500;
          line-height: 20px;
          text-decoration: none;
          border-radius: 6px;
          background: #f9f9f9;
          transition: all 0.2s;
          white-space: nowrap;

          &:hover {
            background: #f0f0f0;
            color: #eb4c10;
          }

          &.active {
            background: #fff5f2;
            color: #eb4c10;
            font-weight: 600;
          }
        }
      }

      .btn-xs.line {
        width: 100%;
        margin-top: 8px;
        padding: 8px 16px;
        border: 1px solid #ebebeb;
        border-radius: 6px;
        background: #fff;
        color: #333;
        font-size: 13px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s;

        &:hover {
          background: #f5f5f5;
        }

        &.login-btn {
          background: var(--orange-300-main, #eb4c10);
          color: #fff;
          border-color: var(--orange-300-main, #eb4c10);

          &:hover {
            background: #d43d0e;
            border-color: #d43d0e;
          }
        }
      }
    }
  }
}

@media (min-width: 500px) and (max-width: 768px) {
  .purchase-ticket {
    .product-card-box {
      .row-box {
        flex: 0 0 calc((100% - 20px) / 2);
        max-width: calc((100% - 20px) / 2);
      }
    }

    .purchase-gift-inline {
      .gift-list {
        .gift-card {
          flex: 0 0 calc((100% - 20px) / 2);
          max-width: calc((100% - 20px) / 2);
        }
      }
    }
  }
}
</style>
